import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { financialYearOf } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, Input, Label, ListSkeleton } from '@tailoring-crm/ui';
import { API_BASE_URL } from '../lib/api/base-url.js';
import { salesRegisterCsvPath } from '../lib/invoices/invoices-api.js';
import { useSalesRegister } from '../lib/invoices/use-invoices.js';
import { formatCurrency, formatDate } from '../lib/format.js';

/**
 * The report an accountant asks for at filing time.
 *
 * It opens on the CURRENT FINANCIAL YEAR to date rather than on this month, because that is the
 * period the question is actually about: a shop's returns, its turnover threshold and its
 * numbering series all run 1 April to 31 March, and defaulting to a calendar month would answer
 * a question nobody asked.
 *
 * A shop that is not GST-registered still gets the report — it is its own sales history — but
 * without a single tax column, for the same reason its bills carry none.
 */
export function SalesRegisterPage(): ReactElement {
  const { t } = useTranslation();

  const defaults = useMemo(() => {
    const now = new Date();
    const [startYear] = financialYearOf(now).split('-');
    return {
      from: `${startYear ?? String(now.getFullYear())}-04-01`,
      to: now.toISOString().slice(0, 10),
    };
  }, []);

  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const register = useSalesRegister({ from, to });
  const data = register.data;
  const registered = data?.registered ?? false;

  return (
    <>
      <PageHeader
        title={t('salesRegister.title')}
        description={t('salesRegister.description')}
        action={
          <Button asChild variant="outline">
            {/* A plain link: the API answers with the file, and the session cookie travels with
                the request exactly as it does for every other call. */}
            <a href={`${API_BASE_URL}${salesRegisterCsvPath({ from, to })}`}>
              <Download aria-hidden="true" />
              {t('salesRegister.export')}
            </a>
          </Button>
        }
      />

      <div className="flex flex-col gap-5">
        <form className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="register-from">{t('salesRegister.from')}</Label>
            <Input
              id="register-from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="register-to">{t('salesRegister.to')}</Label>
            <Input
              id="register-to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
        </form>

        {register.isPending ? <ListSkeleton rows={4} /> : null}

        {data ? (
          <>
            <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div className="rounded-lg border border-border p-3">
                <dt className="text-xs text-muted-foreground">{t('salesRegister.documents')}</dt>
                <dd className="font-semibold tabular-nums">{data.totals.documentCount}</dd>
              </div>
              {registered ? (
                <>
                  <div className="rounded-lg border border-border p-3">
                    <dt className="text-xs text-muted-foreground">{t('salesRegister.taxable')}</dt>
                    <dd className="font-semibold tabular-nums">
                      {formatCurrency(data.totals.taxablePaise)}
                    </dd>
                  </div>
                  <div className="rounded-lg border border-border p-3">
                    <dt className="text-xs text-muted-foreground">{t('salesRegister.tax')}</dt>
                    <dd className="font-semibold tabular-nums">
                      {formatCurrency(data.totals.taxPaise)}
                    </dd>
                  </div>
                </>
              ) : null}
              <div className="rounded-lg border border-border p-3">
                <dt className="text-xs text-muted-foreground">{t('salesRegister.total')}</dt>
                <dd className="font-semibold tabular-nums">
                  {formatCurrency(data.totals.totalPaise)}
                </dd>
              </div>
            </dl>

            {registered && data.byRate.length > 0 ? (
              <section className="flex flex-col gap-2">
                <h2 className="text-sm font-semibold">{t('salesRegister.byRate')}</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground">
                        <th className="p-2">{t('salesRegister.rate')}</th>
                        <th className="p-2 text-right">{t('salesRegister.taxable')}</th>
                        <th className="p-2 text-right">{t('salesRegister.cgst')}</th>
                        <th className="p-2 text-right">{t('salesRegister.sgst')}</th>
                        <th className="p-2 text-right">{t('salesRegister.igst')}</th>
                        <th className="p-2 text-right">{t('salesRegister.tax')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byRate.map((row) => (
                        <tr key={row.ratePercent} className="border-t border-border">
                          <td className="p-2">{row.ratePercent}%</td>
                          <td className="p-2 text-right tabular-nums">
                            {formatCurrency(row.taxablePaise)}
                          </td>
                          <td className="p-2 text-right tabular-nums">
                            {formatCurrency(row.cgstPaise)}
                          </td>
                          <td className="p-2 text-right tabular-nums">
                            {formatCurrency(row.sgstPaise)}
                          </td>
                          <td className="p-2 text-right tabular-nums">
                            {formatCurrency(row.igstPaise)}
                          </td>
                          <td className="p-2 text-right tabular-nums">
                            {formatCurrency(row.taxPaise)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold">{t('salesRegister.documentsHeading')}</h2>
              {data.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('salesRegister.none')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground">
                        <th className="p-2">{t('salesRegister.number')}</th>
                        <th className="p-2">{t('salesRegister.date')}</th>
                        <th className="p-2">{t('salesRegister.customer')}</th>
                        {registered ? (
                          <th className="p-2 text-right">{t('salesRegister.taxable')}</th>
                        ) : null}
                        {registered ? (
                          <th className="p-2 text-right">{t('salesRegister.tax')}</th>
                        ) : null}
                        <th className="p-2 text-right">{t('salesRegister.total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((row) => (
                        <tr key={row.id} className="border-t border-border">
                          <td className="p-2">
                            {row.number}
                            {row.kind === 'credit_note' ? (
                              <span className="ml-1 text-xs text-muted-foreground">
                                {t('invoices.creditNote')}
                              </span>
                            ) : null}
                          </td>
                          <td className="p-2">{formatDate(row.issuedAt)}</td>
                          <td className="p-2">{row.customerName}</td>
                          {registered ? (
                            <td className="p-2 text-right tabular-nums">
                              {formatCurrency(row.taxablePaise)}
                            </td>
                          ) : null}
                          {registered ? (
                            <td className="p-2 text-right tabular-nums">
                              {formatCurrency(row.taxPaise)}
                            </td>
                          ) : null}
                          <td className="p-2 text-right tabular-nums">
                            {formatCurrency(row.totalPaise)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        ) : null}
      </div>
    </>
  );
}

export default SalesRegisterPage;
